import configBase from 'config-base';
import { AppError, CriticalError, ValidationError } from '../Errors';

type EncodedObject = Record<string, string>;

export interface ApiConfig<T extends Record<string, unknown> = any> extends Omit<RequestInit, 'body'> {
  body?: T | RequestInit['body'];
  type?: 'urlencoded' | 'json' | 'form';
  /** skipAuth is intented to prevent authorization header sending if it is present */
  skipAuth?: true;
}

type ApiMethod = (path: string, config?: ApiConfig) => Promise<Response>;

export interface ApiClientProps {
  get: ApiMethod;
  post: ApiMethod;
  put: ApiMethod;
  delete: ApiMethod;
  setHeader(key: string, value: string): void;
  deleteHeader(key: string): void;
}

interface ApiErrorBody {
  code: string;
  message: string;
  status: number;
  timestamp: string;
  error?: string;
}

type TopicRating = {
  topicRatings: {
    [key: string]: {
      error: boolean;
      message: string;
      value: string | null;
    };
  };
};
export type ValidationDetailsBody = {
  [key: string]: {
    error: boolean;
    message: string;
    value: string | null;
  };
} & TopicRating;

type MethodType = 'get' | 'post' | 'put' | 'delete';

export type ErrorInstance = { stack?: string; code?: string | null; message?: string; status?: number };

export type ValidationApiError = ApiErrorBody & { validationDetails: ValidationDetailsBody };

export class ApiError extends AppError {
  public status: number;

  public constructor(response: Response, errorCode?: string | number | null, message?: string) {
    const code: string = errorCode ? errorCode.toString().toLowerCase() : 'api_error';

    super(code, message);
    this.status = response.status;
  }
}

const isNetworkError = (err: unknown) => err && err instanceof TypeError && err.message === 'Network request failed';

const isAuthorizationError = (res: Response) => res.status === 403;

const isValidationError = (res: Response) => res.status === 400;

export const isApiError = (err: unknown): err is ApiError => (err as ApiError).code === 'api_error';

export default class ApiClient implements ApiClientProps {
  private static readonly methods: Array<MethodType> = ['get', 'post', 'put', 'delete'];

  public get!: ApiMethod;
  public post!: ApiMethod;
  public put!: ApiMethod;
  public delete!: ApiMethod;

  private readonly headers: Headers = new Headers({ 'Content-Type': 'application/json' });

  public constructor(private readonly apiClientSideHost: string, private readonly apiServerSideHost: string) {
    ApiClient.methods.forEach(this.createMethod);
  }

  private isOnline(): boolean {
    if (typeof window !== 'undefined' && !navigator.onLine) {
      return false;
    }
    return true;
  }

  public setHeader(key: string, value: string): void {
    this.headers.set(key, value);
  }

  public deleteHeader(key: string): void {
    this.headers.delete(key);
  }

  private readonly createMethod = (method: MethodType) => {
    const newMethod: ApiMethod = async (path, config = {}) => {
      try {
        if (!this.isOnline()) {
          return new Response(null, {
            status: 503,
            statusText: 'Service Unavailable - Offline',
          });
        }

        const { body, ...compatibleConfig } = config;
        const requestConfig: RequestInit = { ...compatibleConfig, method };

        requestConfig.headers = this.prepareHeaders(config);

        if (body) {
          requestConfig.body = this.prepareBody(config);
        }

        const url = this.formatUrl(path);
        const response = await fetch(url, {
          ...requestConfig,
          credentials: 'include',
        });
        if (!response.ok && (config.redirect !== 'manual' || response.status >= 400)) {
          const error = await this.prepareError(response);

          throw error;
        }

        return response;
      } catch (error: any) {
        let err = error;

        if (isNetworkError(err)) {
          err = new CriticalError('network_failed_error');
        }

        throw err;
      }
    };

    this[method] = newMethod;
  };

  private prepareHeaders(config: ApiConfig): Headers {
    // Create current call headers from the default ones
    const currentHeaders = new Headers(this.headers);

    if (config.headers) {
      const providedHeaders = new Headers(config.headers);

      // Apply provided headers and in case override default ones
      for (const [key, value] of providedHeaders.entries()) {
        currentHeaders.set(key, value);
      }
    }

    if (config.type === 'json') {
      currentHeaders.set('Content-Type', 'application/json');
    }

    if (config.type === 'urlencoded') {
      currentHeaders.set('Content-Type', 'application/x-www-form-urlencoded');
    }

    currentHeaders.set('x-site-code', configBase.brandCode);

    // In case form data is passed, the content type header should be taken from it.
    // So we need to clear any content type taken from the default headers or from the passed ones.
    if (config.type === 'form' && config.body instanceof FormData) {
      currentHeaders.delete('Content-Type');
    }

    if (config.skipAuth && currentHeaders.has('Authorization')) {
      currentHeaders.delete('Authorization');
    }

    return currentHeaders;
  }

  private prepareBody({ body, type }: ApiConfig): RequestInit['body'] {
    if (!body) {
      return null;
    }
    if (this.isBodyCustomObj(body)) {
      if (type === 'urlencoded') {
        const encoded = this.encodeBody(body);

        return new URLSearchParams(encoded).toString();
      } else {
        return JSON.stringify(body);
      }
    } else if (type === 'form') {
      return body;
    } else {
      return JSON.stringify(body);
    }
  }

  private async prepareError(response: Response): Promise<ApiError | CriticalError> {
    const authErr = await this.prepareAuthorizationError(response);
    const validationErr = await this.prepareValidationError(response);

    return authErr || validationErr || this.prepareApiError(response);
  }

  private async prepareAuthorizationError(response: Response): Promise<CriticalError | null> {
    return isAuthorizationError(response)
      ? new CriticalError('auth_failed_error', 'user is not authorized', 403)
      : null;
  }

  private async prepareValidationError(response: Response): Promise<ValidationError | null> {
    if (isValidationError(response)) {
      let message: string | undefined = undefined;
      let errorCode: string | null = null;
      let validationDetails: ValidationDetailsBody | undefined;

      try {
        const json: ValidationApiError = await response.json();
        message = json.message || json.error;
        errorCode = json.code.toString();
        validationDetails = json.validationDetails;
      } catch (error: any) {
        if (error instanceof Error) {
          console.warn(error.message);
        }
      }
      return errorCode === '10005' || errorCode === '20000'
        ? new ValidationError(errorCode, message, 400, validationDetails)
        : null;
    }
    return null;
  }

  private async prepareApiError(response: Response): Promise<ApiError> {
    let message: string | undefined = undefined;
    let errorCode: string | null = null;

    try {
      const json: ApiErrorBody = await response.json();
      message = json.message || json.error;
      errorCode = json.code;
    } catch (error: any) {
      if (error instanceof Error) {
        console.warn(error.message);
      }
    }

    return new ApiError(response, errorCode, message);
  }

  private isBodyCustomObj(body: ApiConfig['body']): body is Record<string, string> {
    return (
      typeof body === 'object' &&
      !(body instanceof ArrayBuffer) &&
      !(body instanceof FormData) &&
      !(body instanceof URLSearchParams)
    );
  }

  private encodeBody(body: Record<string, string>): EncodedObject {
    return Object.entries(body).reduce<EncodedObject>((encoded, [key, value]) => {
      encoded[key] = encodeURIComponent(value);

      return encoded;
    }, {});
  }

  private readonly isServer = () => typeof window === 'undefined';

  private readonly adjustPath = (path: string): string => (!path.startsWith('/') ? `/${path}` : path);

  private readonly formatUrl = (path: string): string => {
    const adjustedPath = this.adjustPath(path);
    if (this.isServer()) return this.apiServerSideHost + adjustedPath;
    return this.apiClientSideHost + adjustedPath;
  };
}
